/**
 * Cat & Mouse — the picture doors (specs/cat-mouse-mode.md §7).
 *
 * Three boards of matching shapes teach a child that `K` and `k` are one letter. They do not teach
 * that letters are *for* anything. Every board after them has no letters on its door, only a
 * picture: the targets are the letters of that picture's name, they are collected **in order**, and
 * when the last one lands the train IS the word. That tail is most of the run (`CAT_WORD_BOARDS`).
 *
 * The word comes from the picture pool — the same icon set Picture Words uses, keyed on
 * language-neutral word ids and shared by all ten languages, so the door's picture costs a lookup
 * and no new art.
 *
 * **Korean assembles rather than spells** (§7.3). Its steps are jamo, not blocks, and the train
 * composes them in front of the child: ㄱ + ㅗ → 고, then ㄱ + ㅣ → 기. That is what Hangul *is*, and
 * no other language in this mode can show it. Everything else — Latin, kana, Thai — spells one
 * cell at a time.
 *
 * Phaser-free.
 */
import type { Pack } from '@/data/packs/types';
import { cellsOf } from '@/game/maze/segment';
import { jamoOf, compose, INITIALS, MEDIALS, FINALS } from './hangul';
import { matches, isDenseScript, type LetterClass } from './membership';
import { CAT } from '@/game/config';

export interface DoorWord {
  /** Picture-pool word id — what `wordIconKey`/`wordIconUrl` resolve the door's picture from. */
  id: string;
  /** The finished word, spoken when the door opens. */
  text: string;
  /** What the child collects, in order: one cell per step, or one JAMO per step in Korean. */
  steps: string[];
  /**
   * Which block each step belongs to, parallel to `steps`.
   *
   * Korean needs this and cannot do without it: the steps of `고기` are ㄱ ㅗ ㄱ ㅣ, and a renderer
   * reading that flat list composes ㄱ+ㅗ+ㄱ into **곡** — silently stealing the next block's initial
   * as a batchim. The child would watch their word turn into a different word. Spelling languages
   * set one block per step and never look at it.
   */
  blocks: number[];
  /** True when the steps assemble into blocks rather than lining up (§7.3). */
  composes: boolean;
}

/** Korean only: the jamo of one block, in the order a child writes them. */
function jamoSteps(block: string): string[] | null {
  const j = jamoOf(block);
  if (!j) return null;
  const out = [INITIALS[j.initial], MEDIALS[j.medial]];
  if (j.final > 0) out.push(FINALS[j.final]);
  return out;
}

/**
 * The train after `n` steps.
 *
 * For a spelling language this is just the steps joined. For Korean it is the interesting one: a
 * half-collected block renders as the jamo so far (`ㄱ`), then snaps to the composed syllable the
 * moment its vowel arrives (`고`) — which is the whole point of the Korean ending.
 */
export function renderTrain(word: DoorWord, n: number): string {
  const count = Math.max(0, Math.min(n, word.steps.length));
  const taken = word.steps.slice(0, count);
  if (!word.composes) return taken.join('');

  let out = '';
  let i = 0;
  while (i < taken.length) {
    // Group by BLOCK, never greedily — see DoorWord.blocks for the 곡 bug this prevents.
    const block = word.blocks[i];
    const jamo: string[] = [];
    while (i < taken.length && word.blocks[i] === block) { jamo.push(taken[i]); i++; }
    const initial = INITIALS.indexOf(jamo[0]);
    const medial = jamo.length > 1 ? MEDIALS.indexOf(jamo[1]) : -1;
    if (initial < 0 || medial < 0) { out += jamo.join(''); continue; }
    const final = jamo.length > 2 ? FINALS.indexOf(jamo[2]) : 0;
    out += compose(initial, medial, final > 0 ? final : 0);
  }
  return out;
}

/**
 * Every step of `text` a child would have to collect, with the block each step belongs to, or null
 * if the script cannot supply them.
 */
export function stepsFor(
  text: string, lang: string, composes: boolean,
): { steps: string[]; blocks: number[] } | null {
  const cells = cellsOf(text, lang);
  if (!composes) return { steps: cells, blocks: cells.map((_, i) => i) };

  const steps: string[] = [];
  const blocks: number[] = [];
  cells.forEach((cell, b) => {
    const jamo = jamoSteps(cell);
    if (!jamo) return;
    for (const j of jamo) { steps.push(j); blocks.push(b); }
  });
  // A cell that is not a Hangul block (a loanword's kana, a digit) drops out above, so a word that
  // lost any of its cells is not usable as a composing ending.
  //
  // Indexed rather than `blocks.at(-1)`: `Array.prototype.at` is Safari 15.4, and the build target
  // (es2022) transpiles syntax but never polyfills a built-in, so `.at` shipped raw. This line is
  // Korean-only (`composes`), so on iOS 15.0–15.3 it threw a TypeError inside scene setup and took
  // Cat & Mouse and Space Worm down with it — for the one audience those modes are built for.
  if (steps.length === 0 || blocks[blocks.length - 1] !== cells.length - 1) return null;
  return { steps, blocks };
}

/**
 * Every word in the picture pool this child could be asked to spell: short enough to fit, at least
 * two steps long, and with every step a letter class they have already met.
 *
 * Split out of `pickDoorWord` because the Space Worm wants the same list for a different reason —
 * it picks by LENGTH, one rung at a time (specs/space-worm-mode.md §7.2), where the Cat's picture
 * doors always want the shortest one still unused. One filter, two callers; the filter is the part that is easy to
 * get wrong in a script that composes.
 */
export function wordCandidates(
  pool: Pack | null,
  classes: LetterClass[],
  metClassIds: Set<string>,
  lang: string,
  exclude: Set<string> = new Set(),
  cellCap?: number,
): DoorWord[] {
  if (!pool) return [];
  const composes = lang.toLowerCase().startsWith('ko');
  const cap = cellCap ?? (isDenseScript(lang) ? CAT.wordCellsDense : CAT.wordCells);
  const met = classes.filter((c) => metClassIds.has(c.id));
  if (met.length === 0) return [];

  const candidates: DoorWord[] = [];
  for (const set of pool.sets) {
    for (const w of set.words) {
      if (!w.id || exclude.has(String(w.id))) continue;
      const cells = cellsOf(w.text, lang);
      if (cells.length === 0 || cells.length > cap) continue;
      const parts = stepsFor(w.text, lang, composes);
      if (!parts || parts.steps.length > cap * 2) continue;
      // A one-step "word" is not a word being built — it is one letter with a picture over it, and
      // it would make the ending of the run smaller than any board that led to it. Thai reaches
      // this: `ไก่` is a single grapheme cluster once the leading vowel is merged.
      if (parts.steps.length < 2) continue;
      // Every step must be a letter the child has met — in a composing language the steps are
      // jamo, so this is a check over jamo classes rather than over blocks.
      if (!parts.steps.every((s) => met.some((c) => matches(s, c)))) continue;
      candidates.push({ id: String(w.id), text: w.text, steps: parts.steps, blocks: parts.blocks, composes });
    }
  }
  return candidates;
}

/**
 * Pick a picture door's word: something in the picture pool whose every step is a letter this child
 * has already met, short enough to fit the train.
 *
 * `exclude` holds the word ids this run has already spelled, so a run's picture doors all wear
 * different words rather than the same shortest one over and over.
 *
 * Returns null when nothing qualifies — a first-ever run, where two letters are known. **That is
 * not a bug and it must stay handled**: `deal.ts` falls back to an ordinary two-target board and
 * the picture door waits for the next run. A run that cannot deal its own ending is the failure
 * mode this function invites, and `word.test.ts` is pointed straight at it.
 */
export function pickDoorWord(
  pool: Pack | null,
  classes: LetterClass[],
  metClassIds: Set<string>,
  lang: string,
  rng: () => number,
  exclude: Set<string> = new Set(),
): DoorWord | null {
  const candidates = wordCandidates(pool, classes, metClassIds, lang, exclude);
  if (candidates.length === 0) return null;

  // Shortest first — the ending should land, not turn into a ninth board — then random among the
  // shortest so the same run does not always finish on the same picture.
  const shortest = Math.min(...candidates.map((c) => c.steps.length));
  const best = candidates.filter((c) => c.steps.length === shortest);
  return best[Math.floor(rng() * best.length)];
}
